import { useEffect, useState } from 'react';
import { Gift, Star, Loader2, Zap, ShoppingBag } from 'lucide-react';
import { api } from '../../lib/api';
import { getToken } from '../../lib/auth';
import { Link } from 'react-router-dom';

interface LoyaltyRewardsProps {
  title?: string;
  maxItems?: number;
  showRedeemButton?: boolean;
}

interface Reward {
  id: string;
  name: string;
  description: string | null;
  type: string;
  pointsCost: number;
  value: { amount: number; unit: 'percent' | 'fixed' | 'free' } | null;
  availableQuantity: number | null;
  redeemedCount: number;
  imageUrl: string | null;
}

interface AccountData {
  programId: string;
  pointsBalance: number;
  rewards: Reward[];
}

export function LoyaltyRewardsBlock({ props }: { props: LoyaltyRewardsProps }) {
  const [account, setAccount] = useState<AccountData | null>(null);
  const [loading, setLoading] = useState(true);
  const [redeemingId, setRedeemingId] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const authed = !!getToken();
  const maxItems = props.maxItems ?? 6;

  const load = () => {
    if (!authed) {
      setLoading(false);
      return;
    }
    api
      .get<{ enrolled: boolean; accounts: AccountData[] }>('/customer/loyalty')
      .then((res) => {
        if (res.data.enrolled && res.data.accounts.length > 0) {
          setAccount(res.data.accounts[0]);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, [authed]);

  const handleRedeem = async (reward: Reward) => {
    if (!account) return;
    setRedeemingId(reward.id);
    setMsg(null);
    try {
      const res = await api.post<{ rewardName: string; pointsSpent: number }>(
        '/customer/loyalty/redeem',
        { programId: account.programId, rewardId: reward.id },
      );
      setMsg({
        ok: true,
        text: `Redeemed "${res.data.rewardName}" for ${res.data.pointsSpent} pts!`,
      });
      load();
    } catch {
      setMsg({ ok: false, text: 'Redemption failed. Please try again.' });
    } finally {
      setRedeemingId(null);
    }
  };

  if (!authed) {
    return (
      <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-6 py-8 text-center">
        <Gift size={28} className="mx-auto mb-3 text-zinc-300" />
        <p className="text-sm text-zinc-500">
          <Link to="/login" className="underline hover:no-underline text-zinc-700 font-medium">
            Log in
          </Link>{' '}
          to see your loyalty info
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 size={20} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  const rewards = (account?.rewards ?? []).slice(0, maxItems);

  if (!account || rewards.length === 0) {
    return (
      <div className="bg-zinc-50 border border-zinc-200 rounded-xl px-6 py-8 text-center">
        <Gift size={28} className="mx-auto mb-3 text-zinc-300" />
        <p className="text-sm text-zinc-500">No rewards available yet.</p>
      </div>
    );
  }

  const typeIcon: Record<string, React.ReactNode> = {
    discount: <Zap size={14} />,
    free_item: <Gift size={14} />,
    shipping: <ShoppingBag size={14} />,
    custom: <Star size={14} />,
  };

  return (
    <div className="space-y-4">
      {props.title && <h3 className="text-base font-semibold text-zinc-900">{props.title}</h3>}

      {msg && (
        <div
          className={`rounded-xl px-4 py-3 text-sm flex items-center gap-2 ${
            msg.ok
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
              : 'bg-red-50 border border-red-200 text-red-600'
          }`}
        >
          {msg.text}
          <button
            onClick={() => setMsg(null)}
            className="ml-auto text-xs opacity-60 hover:opacity-100"
          >
            ✕
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {rewards.map((reward) => {
          const canAfford = account.pointsBalance >= reward.pointsCost;
          const outOfStock =
            reward.availableQuantity !== null && reward.redeemedCount >= reward.availableQuantity;
          const valueLabel = reward.value
            ? reward.value.unit === 'percent'
              ? `${reward.value.amount}% off`
              : reward.value.unit === 'free'
                ? 'Free item'
                : `$${reward.value.amount} off`
            : null;

          return (
            <div
              key={reward.id}
              className={`bg-white border rounded-2xl p-4 shadow-sm flex flex-col gap-3 transition-opacity ${!canAfford || outOfStock ? 'opacity-60' : ''}`}
            >
              {reward.imageUrl ? (
                <img
                  src={reward.imageUrl}
                  alt={reward.name}
                  className="w-full h-24 object-cover rounded-xl bg-zinc-100"
                />
              ) : (
                <div className="w-full h-24 rounded-xl bg-zinc-50 flex items-center justify-center text-zinc-300">
                  <Gift size={28} />
                </div>
              )}

              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-1.5 text-zinc-500 text-xs">
                  {typeIcon[reward.type] ?? <Star size={12} />}
                  <span className="capitalize">{reward.type.replace('_', ' ')}</span>
                  {valueLabel && (
                    <>
                      <span className="text-zinc-300">·</span>
                      <span className="font-medium text-zinc-700">{valueLabel}</span>
                    </>
                  )}
                </div>
                <p className="text-sm font-semibold text-zinc-900 leading-snug">{reward.name}</p>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-zinc-900 flex items-center gap-1">
                  <Star size={12} className="text-amber-500" />
                  {reward.pointsCost.toLocaleString()} pts
                </span>
                {props.showRedeemButton !== false &&
                  (outOfStock ? (
                    <span className="text-xs text-zinc-400 font-medium">Out of stock</span>
                  ) : (
                    <button
                      onClick={() => handleRedeem(reward)}
                      disabled={!canAfford || redeemingId === reward.id}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                        canAfford
                          ? 'bg-[--color-primary] text-white hover:opacity-90'
                          : 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                      }`}
                    >
                      {redeemingId === reward.id ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        'Redeem'
                      )}
                    </button>
                  ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
